import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  ErrorPage,
  FeedbackState,
  MetricCard,
  MetricStrip,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
  Progress,
  SettingsSection,
} from "@h2j-data-team/ui";

import { supportCode } from "@/components/error-screen";
import { subscriptionQueryOptions, type SubscriptionDetail } from "@/features/billing/api";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import { groupsQueryOptions } from "@/features/groups/api";
import { membersQueryOptions } from "@/features/organizations/members-api";
import { getStoredActiveOrganizationId } from "@/lib/api-client";

export const Route = createFileRoute("/_authenticated/organization/billing")({
  staticData: { pageTitle: "breadcrumbs.billing" },
  component: BillingPage,
});

// Usage bar colors: calm until 70% of the plan limit, then warning, then danger from 90%.
function usageTone(pct: number) {
  return pct >= 90 ? "danger" : pct >= 70 ? "warning" : "primary";
}

function UsageSection({
  plan,
  usage,
  connectionsCount,
  groupsCount,
  membersCount,
}: {
  plan: SubscriptionDetail["plan"];
  usage: SubscriptionDetail["usage"];
  connectionsCount: number;
  groupsCount: number;
  membersCount: number;
}) {
  const { t } = useTranslation("billing");
  const rows: { label: string; current: number; limit: number }[] = [
    {
      label: t("subscription.jobsThisMonth"),
      current: usage?.jobsThisMonth ?? 0,
      limit: plan.limits.jobsPerMonth,
    },
    {
      label: t("subscription.analysesThisMonth"),
      current: usage?.analysesThisMonth ?? 0,
      limit: plan.limits.analysesPerMonth,
    },
    {
      label: t("subscription.storageAnalysesThisMonth"),
      current: usage?.qvdAnalysesThisMonth ?? 0,
      limit: plan.limits.qvdAnalysesPerMonth,
    },
    {
      label: t("plans.limitConnections"),
      current: connectionsCount,
      limit: plan.limits.connections,
    },
    { label: t("plans.limitGroups"), current: groupsCount, limit: plan.limits.groups },
    { label: t("plans.limitMembers"), current: membersCount, limit: plan.limits.members },
  ];
  return (
    <SettingsSection title={t("subscription.usageThisCycle")}>
      <MetricStrip className="md:grid-cols-3">
        {rows.map((row) => {
          const unlimited = row.limit === -1;
          const pct = unlimited
            ? 0
            : Math.min(100, Math.round((row.current / Math.max(row.limit, 1)) * 100));
          return (
            <MetricCard
              key={row.label}
              label={row.label}
              value={
                <>
                  {row.current}
                  {!unlimited && <span className="text-faint">/ {row.limit}</span>}
                </>
              }
              description={
                unlimited ? undefined : (
                  <Progress value={pct} size="sm" tone={usageTone(pct)} aria-hidden />
                )
              }
            />
          );
        })}
      </MetricStrip>
    </SettingsSection>
  );
}

function BillingPage() {
  const { t } = useTranslation("billing");
  const { t: tNav } = useTranslation("nav");
  const { t: tc } = useTranslation("common");
  const {
    data: subscriptionDetail,
    isLoading: subLoading,
    error: subError,
    refetch,
  } = useQuery(subscriptionQueryOptions());

  // Current resource counts (for the usage-this-cycle bars).
  const activeOrgId = getStoredActiveOrganizationId() ?? "";
  const { data: connectionsList } = useQuery(connectionsQueryOptions);
  const { data: groupsResp } = useQuery(groupsQueryOptions());
  const { data: membersResp } = useQuery(membersQueryOptions(activeOrgId));
  const connectionsCount = connectionsList?.length ?? 0;
  const groupsCount = groupsResp?.data?.length ?? 0;
  const membersCount = membersResp?.data?.members?.length ?? 0;

  const header = (
    <PageHeader eyebrow={t("eyebrow")} title={tNav("planUsage")} description={t("description")} />
  );

  if (subLoading) {
    return <PageSkeleton variant="detail" label={t("loading")} />;
  }

  const noPlanAssigned = (subError as { code?: string } | null)?.code === "SUBSCRIPTION_REQUIRED";

  if (subError && !noPlanAssigned) {
    return (
      <ErrorPage
        kind="load-failed"
        header={header}
        title={tc("error.loadFailed")}
        supportCode={supportCode(subError, tc("error.supportCode"))}
        actions={
          <Button variant="outline" onClick={() => void refetch()}>
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  return (
    <PageShell header={header}>
      <PageContent>
        <div className="flex flex-col gap-6">
          {noPlanAssigned && (
            <FeedbackState
              state="empty"
              title={t("noPlan.title")}
              description={t("noPlan.description")}
            />
          )}
          {subscriptionDetail && (
            <>
              <MetricCard label={t("plans.currentPlan")} value={subscriptionDetail.plan.name} />
              <UsageSection
                plan={subscriptionDetail.plan}
                usage={subscriptionDetail.usage}
                connectionsCount={connectionsCount}
                groupsCount={groupsCount}
                membersCount={membersCount}
              />
            </>
          )}
        </div>
      </PageContent>
    </PageShell>
  );
}
